// components/KPIKRAReports.jsx
import React, { useState, useMemo } from 'react'
import { useDataQuery } from '@dhis2/app-runtime'
import { Card, LinearLoader } from '@dhis2/ui'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts'
import { BarChart3 } from 'lucide-react'

const KPIKRAReports = ({ orgUnitId }) => {
  const [refreshKey, setRefreshKey] = useState(0);

  // FIXED: Use correct API endpoints and data structure
  const { loading, error, data } = useDataQuery({
    objectives: {
      resource: 'events',
      params: {
        orgUnit: orgUnitId,
        program: 'yiT00XKrrHP',
        programStage: 'VIBtpvkpHFR',
        ouMode: 'ACCESSIBLE',
        pageSize: 1000,
        fields: 'event,orgUnit,orgUnitName,eventDate,dataValues[dataElement,value]'
      }
    },
    kpis: {
      resource: 'events',
      params: {
        orgUnit: orgUnitId,
        program: 'yiT00XKrrHP',
        programStage: 'ivyCLjNLFrd',
        ouMode: 'ACCESSIBLE',
        pageSize: 1000,
        fields: 'event,orgUnit,orgUnitName,eventDate,dataValues[dataElement,value]'
      }
    },
    kras: {
      resource: 'events',
      params: {
        orgUnit: orgUnitId,
        program: 'yiT00XKrrHP',
        programStage: 'BJb3qTgbcTF',
        ouMode: 'ACCESSIBLE',
        pageSize: 1000,
        fields: 'event,orgUnit,orgUnitName,eventDate,dataValues[dataElement,value]'
      }
    },
    tasks: {
      resource: 'events',
      params: {
        orgUnit: orgUnitId,
        program: 'yiT00XKrrHP',
        programStage: 'Nv9nYNcBTdM',
        ouMode: 'ACCESSIBLE',
        pageSize: 10000,
        fields: 'event,orgUnit,orgUnitName,eventDate,dataValues[dataElement,value]'
      }
    }
  }, {
    variables: { refreshKey }
  })

  // FIXED: Debug function to check actual data
  const debugData = () => {
    console.log('🔍 KPIKRAReports Data:', {
      objectives: data?.objectives?.events?.length || 0,
      kpis: data?.kpis?.events?.length || 0,
      kras: data?.kras?.events?.length || 0,
      tasks: data?.tasks?.events?.length || 0,
      rawData: data
    });
    
    // Log first KPI data values to see actual structure
    if (data?.kpis?.events?.[0]) {
      console.log('🔍 First KPI:', data.kpis.events[0]);
      console.log('🔍 First KPI DataValues:', data.kpis.events[0].dataValues);
    }
  };

  React.useEffect(() => {
    if (data) {
      debugData();
    }
  }, [data]);

  // Function to extract Objective ID from data values
  const getObjectiveId = (dataValues) => {
    return dataValues?.find(dv => dv.dataElement === 'plJovUlxfQ0')?.value || '';
  };

  // Function to calculate task progress for an objective
  const calculateObjectiveProgress = (objectiveId, allTasks) => {
    if (!allTasks || allTasks.length === 0) return 0;

    // Find tasks linked to this objective
    const objectiveTasks = allTasks.filter(task => {
      const taskObjectiveId = getObjectiveId(task.dataValues);
      return taskObjectiveId === objectiveId;
    });

    if (objectiveTasks.length === 0) return 0;

    // Calculate progress based on task completion status
    const totalTasks = objectiveTasks.length;
    const completedTasks = objectiveTasks.filter(task => {
      const status = task.dataValues?.find(dv => dv.dataElement === 'LuTwfuN34pc')?.value;
      return status === 'completed' || status === 'Completed';
    }).length;

    // Also consider task percentage complete
    const totalProgress = objectiveTasks.reduce((sum, task) => {
      const progressValue = task.dataValues?.find(dv => dv.dataElement === 'AElpNxzTdRh')?.value;
      const progress = parseInt(progressValue) || 0;
      return sum + Math.min(Math.max(progress, 0), 100);
    }, 0);

    const averageProgress = totalProgress / totalTasks;
    
    // Weight completion rate higher than average progress
    const completionRate = (completedTasks / totalTasks) * 100;
    const weightedProgress = (completionRate * 0.7) + (averageProgress * 0.3);
    
    return Math.round(Math.min(weightedProgress, 100));
  };

  // FIXED: Enhanced KPI extraction with proper data access
  const extractKPIs = useMemo(() => {
    if (!data?.kpis?.events) {
      console.log('🟡 No KPI events found');
      return [];
    }
    
    const allObjectives = data?.objectives?.events || [];
    const allTasks = data?.tasks?.events || [];
    
    console.log('🟡 Processing KPIs:', {
      kpiCount: data.kpis.events.length,
      objectiveCount: allObjectives.length,
      taskCount: allTasks.length
    });
    
    const processed = data.kpis.events.map(kpi => {
      const dataValues = kpi.dataValues || [];
      
      // FIXED: Use correct data element IDs from your structure
      const name = dataValues.find(dv => dv.dataElement === 'DBmylDQA9KX')?.value || 'Unnamed KPI';
      const description = dataValues.find(dv => dv.dataElement === 'rQqF75V9t7K')?.value || '';
      const targetValue = dataValues.find(dv => dv.dataElement === 'Ru68Ax2Xo2L')?.value;
      const target = targetValue ? parseInt(targetValue) : 100;
      
      // Get the parent objective ID for this KPI
      const parentObjectiveId = getObjectiveId(dataValues);
            
      
      // Calculate progress based on parent objective's task completion
      const progress = parentObjectiveId ? 
        calculateObjectiveProgress(parentObjectiveId, allTasks) : 0;
      
      // Find the parent objective for additional context
      const parentObjective = allObjectives.find(obj => 
        obj.event === parentObjectiveId || getObjectiveId(obj.dataValues) === parentObjectiveId
      );

      const achievement = Math.min(Math.max(progress, 0), 100);
      
      const linkedTasks = allTasks.filter(task => 
        getObjectiveId(task.dataValues) === parentObjectiveId
      );
      
      const completedTasks = linkedTasks.filter(task => {
        const status = task.dataValues?.find(dv => dv.dataElement === 'LuTwfuN34pc')?.value;
        return status === 'completed' || status === 'Completed';
      });

      return {
        id: kpi.event,
        name,
        description,
        target,
        progress: achievement,
        achievement,
        date: kpi.eventDate,
        parentObjectiveId,
        parentObjectiveName: parentObjective ? 
          parentObjective.dataValues?.find(dv => dv.dataElement === 'EuN5ztFnXsQ')?.value || 'Unknown Objective' : 'No Parent Objective',
        linkedTasks: linkedTasks.length,
        completedTasks: completedTasks.length
      };
    });

    console.log('✅ Processed KPIs:', processed);
    return processed;
  }, [data?.kpis, data?.objectives, data?.tasks]);

  // FIXED: Enhanced KRA extraction
  const extractKRAs = useMemo(() => {
    if (!data?.kras?.events) {
      console.log('🟡 No KRA events found');
      return [];
    }
    
    const kpis = extractKPIs;
    const allObjectives = data?.objectives?.events || [];
    
    const processed = data.kras.events.map(kra => {
      const dataValues = kra.dataValues || [];
      
      // FIXED: Use correct data element IDs from your structure
      const name = dataValues.find(dv => dv.dataElement === 'obapz6Gnyla')?.value || 'Unnamed KRA';
      const description = dataValues.find(dv => dv.dataElement === 'DEECWPTj42E')?.value || '';
      const weightValue = dataValues.find(dv => dv.dataElement === 'Xl4VEFoIFIw')?.value;
      const weight = weightValue ? parseInt(weightValue) : 0;
      
      // Get the parent objective ID for this KRA
      const parentObjectiveId = getObjectiveId(dataValues);
      
      // Find KPIs linked to the same objective
      const linkedKPIs = kpis.filter(kpi => kpi.parentObjectiveId === parentObjectiveId);

      // Calculate KRA progress based on weighted KPI progress
      const kraProgress = linkedKPIs.length > 0 
        ? Math.round(linkedKPIs.reduce((sum, kpi) => sum + kpi.progress, 0) / linkedKPIs.length)
        : 0;

      // Find the parent objective for additional context
      const parentObjective = allObjectives.find(obj => 
        obj.event === parentObjectiveId || getObjectiveId(obj.dataValues) === parentObjectiveId
      );

      return {
        id: kra.event,
        name,
        description,
        weight,
        progress: kraProgress,
        date: kra.eventDate,
        parentObjectiveId,
        parentObjectiveName: parentObjective ? 
          parentObjective.dataValues?.find(dv => dv.dataElement === 'EuN5ztFnXsQ')?.value || 'Unknown Objective' : 'No Parent Objective',
        linkedKPIs: linkedKPIs.length,
        kpiDetails: linkedKPIs
      };
    });

    console.log('✅ Processed KRAs:', processed);
    return processed;
  }, [data?.kras, extractKPIs, data?.objectives]);

  // FIXED: Prepare data for KPI achievement bar chart
  const kpiAchievementData = useMemo(() => {
    const chartData = extractKPIs.map((kpi, index) => ({
      name: kpi.name.length > 12 ? kpi.name.substring(0, 12) + '...' : kpi.name,
      fullName: kpi.name,
      achievement: kpi.achievement,
      target: kpi.target,
      progress: kpi.progress,
      linkedTasks: kpi.linkedTasks,
      completedTasks: kpi.completedTasks,
      parentObjective: kpi.parentObjectiveName,
      color: kpi.achievement >= 80 ? '#00C49F' : 
             kpi.achievement >= 60 ? '#FFBB28' : '#FF8042'
    }));

    console.log('📊 Chart Data:', chartData);
    return chartData;
  }, [extractKPIs]);

  // Calculate overall statistics
  const statistics = useMemo(() => {
    const kpis = extractKPIs;
    const kras = extractKRAs;
    const objectives = data?.objectives?.events || [];
    const tasks = data?.tasks?.events || [];
    
    const totalKPIs = kpis.length;
    const totalKRAs = kras.length;
    const totalObjectives = objectives.length;
    const totalTasks = tasks.length;
    
    // Calculate average progress
    const avgKPIProgress = totalKPIs > 0 
      ? Math.round(kpis.reduce((sum, kpi) => sum + kpi.progress, 0) / totalKPIs)
      : 0;
      
    const avgKRAProgress = totalKRAs > 0
      ? Math.round(kras.reduce((sum, kra) => sum + kra.progress, 0) / totalKRAs)
      : 0;

    // Calculate completion rates
    const completedKPIs = kpis.filter(kpi => kpi.progress >= 100).length;
    const completedKRAs = kras.filter(kra => kra.progress >= 100).length;
    const completedTasks = tasks.filter(task => {
      const status = task.dataValues?.find(dv => dv.dataElement === 'LuTwfuN34pc')?.value;
      return status === 'completed' || status === 'Completed';
    }).length;

    // Calculate task completion rate
    const taskCompletionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

    return {
      totalKPIs,
      totalKRAs,
      totalObjectives,
      totalTasks,
      avgKPIProgress,
      avgKRAProgress,
      completedKPIs,
      completedKRAs,
      completedTasks,
      taskCompletionRate,
      kpiCompletionRate: totalKPIs > 0 ? Math.round((completedKPIs / totalKPIs) * 100) : 0,
      kraCompletionRate: totalKRAs > 0 ? Math.round((completedKRAs / totalKRAs) * 100) : 0
    };
  }, [extractKPIs, extractKRAs, data?.objectives, data?.tasks]);

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  // Color palette
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82ca9d', '#ffc658'];

  if (loading) return (
    <Card style={{ padding: 16 }}>
      <h3>KPI & KRA Performance Dashboard</h3>
      <LinearLoader />
      <div style={{ textAlign: 'center', marginTop: 8, color: '#666' }}>
        Loading performance data...
      </div>
    </Card>
  )

  if (error) {
    console.error('❌ KPIKRAReports Error:', error);
    return (
      <Card style={{ padding: 16, backgroundColor: '#f8d7da' }}>
        <h3>KPI & KRA Performance Dashboard</h3>
        <div style={{ color: '#721c24' }}>
          Error loading performance data: {error.message}
        </div>
        <button 
          onClick={handleRefresh}
          style={{
            marginTop: 12,
            padding: '8px 16px',
            backgroundColor: '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer'
          }}
        >
          Retry
        </button>
      </Card>
    );
  }

  // FIXED: Check if we have data but no KPIs
  if (data && extractKPIs.length === 0) {
    return (
      <Card style={{ padding: 24 }}>
        <h3>KPI & KRA Performance Dashboard</h3>
        <div style={{ 
          textAlign: 'center', 
          padding: 40, 
          color: '#6c757d',
          backgroundColor: '#f8f9fa',
          borderRadius: 8
        }}>
          <div style={{ fontSize: 38, marginBottom: 16 }}><BarChart3 style={{color: 'blue'}} /></div>
          <h4 style={{ margin: '0 0 8px 0' }}>No Performance Data Found</h4>
          <p style={{ margin: 0, marginBottom: 16 }}>
            No KPIs, KRAs, or tasks found for this Organisation.
          </p>
           </div>
      </Card>
    );
  }

  return (
    <Card style={{ padding: 16, marginBottom: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <button 
          onClick={handleRefresh}
          style={{
            padding: '8px 16px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
            fontSize: 14
          }}
        >
          Refresh Data
        </button>
      </div>
      
      {/* Summary Statistics */}
      <div style={{ 
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 12,
        marginBottom: 24
      }}>
        <div style={{ 
          padding: 16,
          backgroundColor: '#e7f3ff',
          borderRadius: 8,
          textAlign: 'center',
          border: '1px solid #b3d9ff'
        }}>
          <div style={{ fontWeight: '600', color: '#0088FE', fontSize: 12 }}>TOTAL KPIs</div>
          <div style={{ fontSize: 24, fontWeight: 'bold', color: '#0056b3' }}>{statistics.totalKPIs}</div>
          <div style={{ fontSize: 12, color: '#666' }}>{statistics.completedKPIs} completed</div>
        </div>
        
        <div style={{ 
          padding: 16,
          backgroundColor: '#e6f7f0',
          borderRadius: 8,
          textAlign: 'center',
          border: '1px solid #a3e9cb'
        }}>
          <div style={{ fontWeight: '600', color: '#00C49F', fontSize: 12 }}>AVG KPI PROGRESS</div>
          <div style={{ fontSize: 24, fontWeight: 'bold', color: '#00875a' }}>{statistics.avgKPIProgress}%</div>
          <div style={{ fontSize: 12, color: '#666' }}>{statistics.kpiCompletionRate}% completion rate</div>
        </div>
        
        <div style={{ 
          padding: 16,
          backgroundColor: '#fff4e6',
          borderRadius: 8,
          textAlign: 'center',
          border: '1px solid #ffd8a8'
        }}>
          <div style={{ fontWeight: '600', color: '#FFBB28', fontSize: 12 }}>TOTAL KRAs</div>
          <div style={{ fontSize: 24, fontWeight: 'bold', color: '#e67700' }}>{statistics.totalKRAs}</div>
          <div style={{ fontSize: 12, color: '#666' }}>{statistics.completedKRAs} completed</div>
        </div>
        
        <div style={{ 
          padding: 16,
          backgroundColor: '#ffe6e6',
          borderRadius: 8,
          textAlign: 'center',
          border: '1px solid #ffb3b3'
        }}>
          <div style={{ fontWeight: '600', color: '#FF8042', fontSize: 12 }}>TASK COMPLETION</div>
          <div style={{ fontSize: 24, fontWeight: 'bold', color: '#cc0000' }}>{statistics.taskCompletionRate}%</div>
          <div style={{ fontSize: 12, color: '#666' }}>{statistics.completedTasks}/{statistics.totalTasks} tasks</div>
        </div>
      </div>
      
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 24 }}>
        {/* KPI Section - Bar Chart */}
        <div>
          <h4 style={{ marginBottom: 16 }}>Key Performance Indicators ({statistics.totalKPIs})</h4>
          {extractKPIs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#6c757d', backgroundColor: '#f8f9fa', borderRadius: 8 }}>
              <div style={{ fontSize: 48, marginBottom: 8 }}><BarChart3 /></div>
              <div style={{ fontWeight: '600', marginBottom: 4 }}>No KPIs Found</div>
              <div>Create KPIs to track performance metrics</div>
            </div>
          ) : (
            <>
              {/* Bar Chart for KPI Achievement */}
              <div style={{ height: 300, marginBottom: 16 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={kpiAchievementData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="name" 
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      fontSize={12}
                    />
                    <YAxis 
                      domain={[0, 100]}
                      label={{ 
                        value: 'Progress (%)', 
                        angle: -90, 
                        position: 'insideLeft',
                        offset: -10,
                        style: { textAnchor: 'middle' }
                      }}
                    />
                    <Tooltip 
                      formatter={(value, name) => {
                        if (name === 'achievement') {
                          return [`${value}%`, 'Progress'];
                        }
                        return [value, name];
                      }}
                      labelFormatter={(value, payload) => {
                        if (payload && payload[0]) {
                          const data = payload[0].payload;
                          return (
                            <div style={{ padding: 8, minWidth: 200 }}>
                              <strong style={{ fontSize: 14 }}>{data.fullName}</strong>
                              <div style={{ marginTop: 4, fontSize: 12, color: '#666' }}>
                                Parent Objective: {data.parentObjective}
                              </div>
                              <div style={{ marginTop: 4 }}>Progress: <strong>{data.achievement}%</strong></div>
                              <div>Target: {data.target}%</div>
                              <div>Tasks: {data.completedTasks}/{data.linkedTasks} completed</div>
                            </div>
                          );
                        }
                        return value;
                      }}
                    />
                    <Bar 
                      dataKey="achievement" 
                      name="Progress (%)"
                      radius={[4, 4, 0, 0]}
                    >
                      {kpiAchievementData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </>
          )}
        </div>

        {/* KRA Section - Enhanced Cards with Progress 
        <div>
          <h4 style={{ marginBottom: 16 }}>Key Result Areas ({statistics.totalKRAs})</h4>
          {extractKRAs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#6c757d', backgroundColor: '#f8f9fa', borderRadius: 8 }}>
              <div style={{ fontSize: 48, marginBottom: 8 }}>🎯</div>
              <div style={{ fontWeight: '600', marginBottom: 4 }}>No KRAs Found</div>
              <div>Create KRAs to track key result areas</div>
            </div>
          ) : (
            <div style={{ maxHeight: 400, overflowY: 'auto' }}>
              {extractKRAs.map((kra, index) => (
                <div key={kra.id} style={{ 
                  padding: 16, 
                  marginBottom: 12, 
                  border: '1px solid #e9ecef',
                  borderRadius: 8,
                  borderLeft: `4px solid ${COLORS[index % COLORS.length]}`,
                  backgroundColor: '#f8f9fa'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start',
                    marginBottom: 8 
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', fontSize: 14, marginBottom: 4 }}>
                        {kra.name}
                      </div>
                      <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
                        Parent: {kra.parentObjectiveName}
                      </div>
                     
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          fontSize: 12, 
                          marginBottom: 4 
                        }}>
                          <span>Overall Progress</span>
                          <span>{kra.progress}%</span>
                        </div>
                        <div style={{
                          width: '100%',
                          height: 6,
                          backgroundColor: '#e9ecef',
                          borderRadius: 3,
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${kra.progress}%`,
                            height: '100%',
                            backgroundColor: COLORS[index % COLORS.length],
                            borderRadius: 3,
                            transition: 'width 0.3s ease'
                          }} />
                        </div>
                      </div>
                    </div>
                    <div style={{ 
                      padding: '4px 8px',
                      backgroundColor: COLORS[index % COLORS.length],
                      color: 'white',
                      borderRadius: 12,
                      fontSize: 12,
                      fontWeight: 'bold',
                      minWidth: 50,
                      textAlign: 'center'
                    }}>
                      {kra.weight}%
                    </div>
                  </div>
                  
                  {kra.description && (
                    <div style={{ 
                      fontSize: 12, 
                      color: '#6c757d', 
                      lineHeight: 1.4,
                      marginBottom: 8
                    }}>
                      {kra.description}
                    </div>
                  )}
                  
                  <div style={{ 
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: 11,
                    color: '#6c757d'
                  }}>
                    <div>
                      {kra.linkedKPIs} linked KPIs • Created: {new Date(kra.date).toLocaleDateString()}
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center' 
                    }}>
                      <div 
                        style={{
                          width: 8,
                          height: 8,
                          backgroundColor: COLORS[index % COLORS.length],
                          borderRadius: '50%',
                          marginRight: 4
                        }}
                      />
                      Weight
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        */ }
      </div>
    </Card>
  )
}

export default KPIKRAReports